import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import { changeUserPassword, fetchUserSettings, updateUserPreferences, updateUserProfile } from '../settingsApi';
import { useLanguage } from '../i18n/language';
import './SettingsPage.css';

const ROLE_COLORS = {
  admin: {
    headerBg: '#00334a',
    iconBg: '#02c3a6',
    accent: '#02c3a6',
    accentLight: '#e0f7f4',
    accentText: '#00334a',
  },
  member: {
    headerBg: '#00334a',
    iconBg: '#02c3a6',
    accent: '#02c3a6',
    accentLight: '#e0f7f4',
    accentText: '#00334a',
  },
  shareholder: {
    headerBg: '#00334a',
    iconBg: '#02c3a6',
    accent: '#02c3a6',
    accentLight: '#e0f7f4',
    accentText: '#00334a',
  },
};

/** Bilingual role badge + avatar initials */
const ROLE_COPY = {
  admin: {
    roleBadge: { ar: 'مسؤول النظام', en: 'System administrator' },
    initials: { ar: 'أس', en: 'SY' },
  },
  member: {
    roleBadge: { ar: 'عضو مجلس الإدارة', en: 'Board member' },
    initials: { ar: 'عم', en: 'BM' },
  },
  shareholder: {
    roleBadge: { ar: 'مساهم', en: 'Shareholder' },
    initials: { ar: 'مس', en: 'SH' },
  },
};

const SECTIONS = [
  { key: 'profile', icon: 'user' },
  { key: 'security', icon: 'lock' },
  { key: 'notifications', icon: 'bell' },
  { key: 'display', icon: 'settings' },
  { key: 'about', icon: 'info' },
];

const PROFILE_DEFAULTS = {
  admin: { name: 'الرئيس التنفيذي', email: 'secretary@corppass.sa', phone: '0500000001', title: 'الرئيس التنفيذي مجلس الإدارة', branch: 'شركة كورب باس' },
  member: { name: 'محمد العمري', email: 'member@corppass.sa', phone: '0500000002', title: 'عضو مجلس الإدارة', branch: 'شركة الفجر' },
  shareholder: { name: 'فهد القحطاني', email: 'partner@corppass.sa', phone: '0500000003', title: 'شريك ومساهم رئيسي', branch: 'شركة النور' },
};

function Toast({ msg, type }) {
  return (
    <div className={`st-toast st-toast--${type}`}>
      <Icon name={type === 'success' ? 'check-circle' : 'x-circle'} size={15} color="#fff" />
      {msg}
    </div>
  );
}

export default function SettingsPage({ role = 'admin' }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const colors = ROLE_COLORS[role];
  const roleText = ROLE_COPY[role];
  const theme = useMemo(() => ({
    ...colors,
    label: isAr ? roleText.roleBadge.ar : roleText.roleBadge.en,
    initials: isAr ? roleText.initials.ar : roleText.initials.en,
  }), [colors, roleText, isAr]);

  const tx = useMemo(() => ({
    settings: isAr ? 'الإعدادات' : 'Settings',
    settingsSub: isAr ? 'إدارة حسابك وتفضيلات النظام' : 'Manage your account and system preferences',
    loading: isAr ? 'جارٍ تحميل الإعدادات…' : 'Loading settings...',
    loadFailed: isAr ? 'تعذر تحميل الإعدادات' : 'Unable to load settings',
    saveFailed: isAr ? 'تعذر حفظ الإعدادات' : 'Unable to save settings',
    nameEmailRequired: isAr ? 'الاسم والبريد الإلكتروني مطلوبان' : 'Name and email are required',
    profileSaved: isAr ? 'تم حفظ بيانات الملف الشخصي بنجاح' : 'Profile saved successfully',
    profileSaveFailed: isAr ? 'تعذر حفظ الملف الشخصي' : 'Unable to save profile',
    pwChanged: isAr ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully',
    pwChangeFailed: isAr ? 'تعذر تغيير كلمة المرور' : 'Unable to change password',
    pwCurrentRequired: isAr ? 'كلمة المرور الحالية مطلوبة' : 'Current password is required',
    pwMin8: isAr ? 'يجب أن تكون 8 أحرف على الأقل' : 'Must be at least 8 characters',
    pwMismatch: isAr ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match',
    sessionEnded: isAr ? 'تم إنهاء الجلسة' : 'Session ended',
    breadcrumbJoin: isAr ? 'الإعدادات' : 'Settings',

    sectionProfile: isAr ? 'الملف الشخصي' : 'Profile',
    sectionSecurity: isAr ? 'الأمان وكلمة المرور' : 'Security & Password',
    sectionNotifications: isAr ? 'الإشعارات' : 'Notifications',
    sectionDisplay: isAr ? 'العرض والتفضيلات' : 'Display & Preferences',
    sectionAbout: isAr ? 'حول النظام' : 'About',

    save: isAr ? 'حفظ' : 'Save',
    notifsSaved: isAr ? 'تم حفظ إعدادات الإشعارات' : 'Notification settings saved',
    saveNotifPrefs: isAr ? 'حفظ تفضيلات الإشعارات' : 'Save notification preferences',
    displaySaved: isAr ? 'تم حفظ تفضيلات العرض' : 'Display preferences saved',
    securitySaved: isAr ? 'تم حفظ إعدادات الأمان' : 'Security settings saved',
    avatarChange: isAr ? 'تغيير الصورة' : 'Change photo',
    fullName: isAr ? 'الاسم الكامل' : 'Full Name',
    email: isAr ? 'البريد الإلكتروني' : 'Email',
    mobile: isAr ? 'رقم الجوال' : 'Mobile Number',
    jobTitle: isAr ? 'المسمى الوظيفي' : 'Job Title',
    branch: isAr ? 'الشركة' : 'Branch',
    saveChanges: isAr ? 'حفظ التغييرات' : 'Save Changes',
    revert: isAr ? 'تراجع' : 'Revert',
    phFullName: isAr ? 'أدخل اسمك الكامل' : 'Enter your full name',
    phEmail: isAr ? 'example@branch.com' : 'example@branch.com',
    phPhone: isAr ? '05xxxxxxxx' : '05xxxxxxxx',
    phJobTitle: isAr ? 'المسمى الوظيفي' : 'Job title',
    phBranch: isAr ? 'اسم الشركة' : 'Branch name',

    changePassword: isAr ? 'تغيير كلمة المرور' : 'Change Password',
    currentPassword: isAr ? 'كلمة المرور الحالية' : 'Current Password',
    newPassword: isAr ? 'كلمة المرور الجديدة' : 'New Password',
    confirmPassword: isAr ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password',
    phNewPwMin: isAr ? '8 أحرف على الأقل' : 'At least 8 characters',
    phConfirmPw: isAr ? 'أعد كتابة كلمة المرور' : 'Re-enter new password',
    pwWeak: isAr ? 'ضعيف' : 'Weak',
    pwFair: isAr ? 'متوسط' : 'Fair',
    pwStrong: isAr ? 'قوي' : 'Strong',
    pwExcellent: isAr ? 'ممتاز' : 'Excellent',

    securitySettings: isAr ? 'إعدادات الأمان' : 'Security Settings',
    twoFA: isAr ? 'المصادقة الثنائية (2FA)' : 'Two-factor authentication (2FA)',
    twoFASub: isAr ? 'طبقة أمان إضافية عند تسجيل الدخول' : 'Extra security when signing in',
    sessionLoginAlert: isAr ? 'تنبيه عند تسجيل الدخول من جهاز جديد' : 'Alert on new device sign-in',
    sessionLoginAlertSub: isAr ? 'استقبل بريد إلكتروني عند كل دخول غير مألوف' : 'Email when a sign-in looks unfamiliar',

    activeSessions: isAr ? 'الجلسات النشطة' : 'Active Sessions',
    active: isAr ? 'نشط' : 'Active',
    end: isAr ? 'إنهاء' : 'End',

    languageRegion: isAr ? 'اللغة والمنطقة' : 'Language and Region',
    interfaceLang: isAr ? 'لغة الواجهة' : 'Interface Language',
    langArabic: isAr ? 'العربية' : 'Arabic',
    langEnglish: isAr ? 'الإنجليزية' : 'English',
    dateFormat: isAr ? 'تنسيق التاريخ' : 'Date Format',
    dateFmtDMY: isAr ? 'يوم/شهر/سنة' : 'DD/MM/YYYY',
    dateFmtMDY: isAr ? 'شهر/يوم/سنة' : 'MM/DD/YYYY',
    dateFmtYMD: isAr ? 'سنة-شهر-يوم' : 'YYYY-MM-DD',
    timezone: isAr ? 'المنطقة الزمنية' : 'Time Zone',
    tzRiyadh: 'Asia/Riyadh (UTC+3)',
    tzDubai: 'Asia/Dubai (UTC+4)',
    tzCairo: 'Africa/Cairo (UTC+2)',

    systemAppearance: isAr ? 'مظهر النظام' : 'System Appearance',
    light: isAr ? 'فاتح' : 'Light',
    auto: isAr ? 'تلقائي' : 'Auto',
    dark: isAr ? 'داكن' : 'Dark',
    animations: isAr ? 'تشغيل التأثيرات والحركات' : 'Enable animations',
    animationsSub: isAr ? 'تأثيرات بصرية عند التنقل وتحميل الصفحات' : 'Visual effects when navigating and loading pages',
    compact: isAr ? 'وضع العرض المضغوط' : 'Compact mode',
    compactSub: isAr ? 'تقليل المسافات لعرض محتوى أكثر في الشاشة' : 'Reduce spacing to fit more on screen',

    aboutTagline: isAr ? 'نصادق قرارات الشركات' : 'We certify branch decisions',
    version: isAr ? 'الإصدار' : 'Version',
    releaseDate: isAr ? 'تاريخ الإصدار' : 'Release Date',
    license: isAr ? 'نوع الترخيص' : 'License',
    currentRole: isAr ? 'الدور الحالي' : 'Current Role',
    developer: isAr ? 'المطور' : 'Developer',
    support: isAr ? 'الدعم الفني' : 'Support',
    developerTeam: isAr ? 'فريق كورب باس للتقنية' : 'Corp Pass Engineering Team',
    releaseDateVal: isAr ? '9 أبريل 2026' : '9 Apr 2026',
    logoAlt: isAr ? 'كورب باس' : 'Corp Pass',

    privacy: isAr ? 'سياسة الخصوصية' : 'Privacy Policy',
    terms: isAr ? 'شروط الاستخدام' : 'Terms of Use',
    contactSupport: isAr ? 'تواصل مع الدعم' : 'Contact Support',

    notifGroupEmail: isAr ? 'البريد الإلكتروني' : 'Email',
    notifGroupSms: isAr ? 'الرسائل النصية' : 'SMS',
    notifGroupPush: isAr ? 'الإشعارات الفورية' : 'Push notifications',
    notifEmailNewDecision: isAr ? 'قرارات جديدة تحتاج تصويتك' : 'New decisions awaiting your vote',
    notifEmailVoteResult: isAr ? 'نتائج التصويت النهائية' : 'Final voting results',
    notifEmailDeadline: isAr ? 'تذكير قبل انتهاء التصويت' : 'Reminder before voting ends',
    notifSmsNew: isAr ? 'إشعار قرار جديد' : 'New decision alert',
    notifSmsDeadline: isAr ? 'تذكير اليوم الأخير للتصويت' : 'Last day to vote reminder',
    notifPushNew: isAr ? 'قرارات جديدة' : 'New decisions',
    notifPushVote: isAr ? 'نتيجة التصويت' : 'Vote result',
  }), [isAr]);

  const sectionLabel = (key) => {
    const map = {
      profile: tx.sectionProfile,
      security: tx.sectionSecurity,
      notifications: tx.sectionNotifications,
      display: tx.sectionDisplay,
      about: tx.sectionAbout,
    };
    return map[key] ?? key;
  };

  const notifRows = useMemo(() => [
    {
      group: tx.notifGroupEmail,
      items: [
        { key: 'email_new_decision', label: tx.notifEmailNewDecision },
        { key: 'email_vote_result', label: tx.notifEmailVoteResult },
        { key: 'email_deadline', label: tx.notifEmailDeadline },
      ],
    },
    {
      group: tx.notifGroupSms,
      items: [
        { key: 'sms_new_decision', label: tx.notifSmsNew },
        { key: 'sms_deadline', label: tx.notifSmsDeadline },
      ],
    },
    {
      group: tx.notifGroupPush,
      items: [
        { key: 'push_new_decision', label: tx.notifPushNew },
        { key: 'push_vote_result', label: tx.notifPushVote },
      ],
    },
  ], [tx]);

  const sessionRows = useMemo(() => [
    {
      device: 'Chrome · macOS',
      loc: isAr ? 'الرياض، السعودية' : 'Riyadh, Saudi Arabia',
      time: isAr ? 'الآن' : 'Now',
      active: true,
    },
    {
      device: 'Safari · iPhone 15',
      loc: isAr ? 'جدة، السعودية' : 'Jeddah, Saudi Arabia',
      time: isAr ? 'منذ ساعتين' : '2 hours ago',
      active: false,
    },
    {
      device: 'Edge · Windows 11',
      loc: isAr ? 'الدمام، السعودية' : 'Dammam, Saudi Arabia',
      time: isAr ? 'أمس 10:30 ص' : 'Yesterday 10:30 AM',
      active: false,
    },
  ], [isAr]);

  const pwStrengthSegments = useMemo(() => [
    tx.pwWeak,
    tx.pwFair,
    tx.pwStrong,
    tx.pwExcellent,
  ], [tx]);

  const defProf = PROFILE_DEFAULTS[role];

  const [activeSection, setActive] = useState('profile');
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [profile, setProfile] = useState({ ...defProf });
  const [profEdit, setProfEdit] = useState({ ...defProf });

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [twoFA, setTwoFA] = useState(false);
  const [sessionAlert, setSessionAlert] = useState(true);

  const [notifs, setNotifs] = useState({
    email_new_decision: true,
    email_vote_result: true,
    email_deadline: false,
    sms_new_decision: false,
    sms_deadline: true,
    push_new_decision: true,
    push_vote_result: false,
  });

  const [lang, setLang] = useState('ar');
  const [compact, setCompact] = useState(false);
  const [animations, setAnimations] = useState(true);

  const avatarRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await fetchUserSettings();
        if (cancelled) return;
        const nextProfile = {
          name: data.name || defProf.name,
          email: data.email || defProf.email,
          phone: data.phone || '',
          title: data.title || defProf.title,
          branch: data.branch || defProf.branch,
        };
        setProfile(nextProfile);
        setProfEdit(nextProfile);
        setNotifs({
          email_new_decision: Boolean(data.notifications?.emailNewDecision),
          email_vote_result: Boolean(data.notifications?.emailVoteResult),
          email_deadline: Boolean(data.notifications?.emailDeadline),
          sms_new_decision: Boolean(data.notifications?.smsNewDecision),
          sms_deadline: Boolean(data.notifications?.smsDeadline),
          push_new_decision: Boolean(data.notifications?.pushNewDecision),
          push_vote_result: Boolean(data.notifications?.pushVoteResult),
        });
        setLang(data.display?.lang || 'ar');
        setCompact(Boolean(data.display?.compact));
        setAnimations(Boolean(data.display?.animations));
        setTwoFA(Boolean(data.security?.twoFA));
        setSessionAlert(Boolean(data.security?.sessionAlert));
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : tx.loadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [defProf, tx.loadFailed]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const handleProfChange = (e) => setProfEdit((p) => ({ ...p, [e.target.name]: e.target.value }));

  const savePreferences = async (successMsg) => {
    try {
      await updateUserPreferences({
        notifications: {
          emailNewDecision: notifs.email_new_decision,
          emailVoteResult: notifs.email_vote_result,
          emailDeadline: notifs.email_deadline,
          smsNewDecision: notifs.sms_new_decision,
          smsDeadline: notifs.sms_deadline,
          pushNewDecision: notifs.push_new_decision,
          pushVoteResult: notifs.push_vote_result,
        },
        display: { lang, compact, animations },
        security: { twoFA, sessionAlert },
      });
      showToast(successMsg);
    } catch (e) {
      showToast(e instanceof Error ? e.message : tx.saveFailed, 'error');
    }
  };

  const saveProfile = async () => {
    if (!profEdit.name.trim() || !profEdit.email.trim()) {
      showToast(tx.nameEmailRequired, 'error');
      return;
    }
    try {
      const updated = await updateUserProfile({
        name: profEdit.name,
        email: profEdit.email,
        phone: profEdit.phone || null,
        title: profEdit.title || null,
      });
      const nextProfile = {
        name: updated.name || profEdit.name,
        email: updated.email || profEdit.email,
        phone: updated.phone || '',
        title: updated.title || '',
        branch: updated.branch || profile.branch,
      };
      setProfile(nextProfile);
      setProfEdit(nextProfile);
      showToast(tx.profileSaved);
    } catch (e) {
      showToast(e instanceof Error ? e.message : tx.profileSaveFailed, 'error');
    }
  };

  const savePw = async () => {
    const errs = {};
    if (!pwForm.current) errs.current = tx.pwCurrentRequired;
    if (pwForm.next.length < 8) errs.next = tx.pwMin8;
    if (pwForm.next !== pwForm.confirm) errs.confirm = tx.pwMismatch;
    setPwErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      await changeUserPassword({
        currentPassword: pwForm.current,
        newPassword: pwForm.next,
      });
      setPwForm({ current: '', next: '', confirm: '' });
      showToast(tx.pwChanged);
    } catch (e) {
      showToast(e instanceof Error ? e.message : tx.pwChangeFailed, 'error');
    }
  };

  const saveNotifs = () => savePreferences(tx.notifsSaved);

  const pwStrengthLabel = () => {
    if (pwForm.next.length === 0) return '—';
    if (pwForm.next.length < 4) return tx.pwWeak;
    if (pwForm.next.length < 7) return tx.pwFair;
    if (pwForm.next.length < 10) return tx.pwStrong;
    return tx.pwExcellent;
  };

  const renderProfile = () => (
    <div className="st-section">
      <div className="st-avatar-area">
        <div className="st-avatar-wrap">
          <div className="st-avatar" style={{ background: theme.iconBg }}>{theme.initials}</div>
          <button type="button" className="st-avatar-edit" style={{ background: theme.accent }}
            onClick={() => avatarRef.current?.click()} title={tx.avatarChange}>
            <Icon name="edit" size={12} color="#fff" />
          </button>
          <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} />
        </div>
        <div className="st-avatar-info">
          <div className="st-avatar-name">{profile.name}</div>
          <div className="st-avatar-role" style={{ background: theme.accentLight, color: theme.accentText }}>
            {theme.label}
          </div>
          <div className="st-avatar-branch">{profile.branch}</div>
        </div>
      </div>

      <div className="st-divider" />

      <div className="st-form-grid">
        <div className="st-field">
          <label className="st-label">{tx.fullName} <span>*</span></label>
          <input className="st-input" name="name" value={profEdit.name} onChange={handleProfChange} placeholder={tx.phFullName} />
        </div>
        <div className="st-field">
          <label className="st-label">{tx.email} <span>*</span></label>
          <input className="st-input" name="email" type="email" value={profEdit.email} onChange={handleProfChange} placeholder={tx.phEmail} />
        </div>
        <div className="st-field">
          <label className="st-label">{tx.mobile}</label>
          <input className="st-input" name="phone" value={profEdit.phone} onChange={handleProfChange} placeholder={tx.phPhone} />
        </div>
        <div className="st-field">
          <label className="st-label">{tx.jobTitle}</label>
          <input className="st-input" name="title" value={profEdit.title} onChange={handleProfChange} placeholder={tx.phJobTitle} />
        </div>
        <div className="st-field st-field--full">
          <label className="st-label">{tx.branch}</label>
          <input className="st-input" name="branch" value={profEdit.branch} onChange={handleProfChange} placeholder={tx.phBranch} />
        </div>
      </div>

      <div className="st-form-actions">
        <button type="button" className="st-btn st-btn--primary" style={{ background: theme.accent }} onClick={saveProfile}>
          <Icon name="check" size={14} color="#fff" /> {tx.saveChanges}
        </button>
        <button type="button" className="st-btn st-btn--ghost" onClick={() => setProfEdit({ ...profile })}>
          {tx.revert}
        </button>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="st-section">
      <div className="st-card">
        <div className="st-card-head" style={{ '--st-accent': theme.accent }}>
          <span className="st-card-icon" style={{ background: theme.iconBg }}>
            <Icon name="lock" size={14} color="#fff" />
          </span>
          {tx.changePassword}
        </div>
        <div className="st-form-grid">
          <div className="st-field st-field--full">
            <label className="st-label">{tx.currentPassword}</label>
            <input
              className={`st-input${pwErrors.current ? ' st-input--error' : ''}`}
              type="password"
              placeholder="••••••••"
              value={pwForm.current}
              onChange={(e) => { setPwErrors((p) => ({ ...p, current: '' })); setPwForm((p) => ({ ...p, current: e.target.value })); }}
            />
            {pwErrors.current && <span className="st-error">{pwErrors.current}</span>}
          </div>
          <div className="st-field">
            <label className="st-label">{tx.newPassword}</label>
            <input
              className={`st-input${pwErrors.next ? ' st-input--error' : ''}`}
              type="password"
              placeholder={tx.phNewPwMin}
              value={pwForm.next}
              onChange={(e) => { setPwErrors((p) => ({ ...p, next: '' })); setPwForm((p) => ({ ...p, next: e.target.value })); }}
            />
            {pwErrors.next && <span className="st-error">{pwErrors.next}</span>}
          </div>
          <div className="st-field">
            <label className="st-label">{tx.confirmPassword}</label>
            <input
              className={`st-input${pwErrors.confirm ? ' st-input--error' : ''}`}
              type="password"
              placeholder={tx.phConfirmPw}
              value={pwForm.confirm}
              onChange={(e) => { setPwErrors((p) => ({ ...p, confirm: '' })); setPwForm((p) => ({ ...p, confirm: e.target.value })); }}
            />
            {pwErrors.confirm && <span className="st-error">{pwErrors.confirm}</span>}
          </div>
        </div>
        <div className="st-pw-strength">
          {pwStrengthSegments.map((lbl, i) => (
            <div
              key={lbl}
              className={`st-pw-seg${pwForm.next.length > i * 3 ? ' st-pw-seg--on' : ''}`}
              style={pwForm.next.length > i * 3 ? { background: ['#e53935', '#fb8c00', '#02c3a6', '#00334a'][i] } : {}}
            />
          ))}
          <span className="st-pw-label">{pwStrengthLabel()}</span>
        </div>
        <div className="st-form-actions">
          <button type="button" className="st-btn st-btn--primary" style={{ background: theme.accent }} onClick={savePw}>
            <Icon name="check" size={14} color="#fff" /> {tx.changePassword}
          </button>
        </div>
      </div>

      <div className="st-card">
        <div className="st-card-head">
          <span className="st-card-icon" style={{ background: theme.iconBg }}>
            <Icon name="shield" size={14} color="#fff" />
          </span>
          {tx.securitySettings}
        </div>
        <div className="st-toggles">
          <ToggleRow
            label={tx.twoFA}
            sub={tx.twoFASub}
            checked={twoFA}
            onChange={setTwoFA}
            accent={theme.accent}
          />
          <ToggleRow
            label={tx.sessionLoginAlert}
            sub={tx.sessionLoginAlertSub}
            checked={sessionAlert}
            onChange={setSessionAlert}
            accent={theme.accent}
          />
        </div>
        <div className="st-form-actions">
          <button type="button" className="st-btn st-btn--primary" style={{ background: theme.accent }}
            onClick={() => savePreferences(tx.securitySaved)}>
            <Icon name="check" size={14} color="#fff" /> {tx.save}
          </button>
        </div>
      </div>

      <div className="st-card">
        <div className="st-card-head">
          <span className="st-card-icon" style={{ background: theme.iconBg }}>
            <Icon name="eye" size={14} color="#fff" />
          </span>
          {tx.activeSessions}
        </div>
        {sessionRows.map((s, i) => (
          <div key={i} className="st-session">
            <div className="st-session-icon" style={{ background: s.active ? theme.accentLight : '#f5f7fa' }}>
              <Icon name="settings" size={14} color={s.active ? theme.accent : '#90a4ae'} />
            </div>
            <div className="st-session-info">
              <div className="st-session-device">{s.device}</div>
              <div className="st-session-meta">{s.loc} · {s.time}</div>
            </div>
            {s.active
              ? <span className="st-session-badge" style={{ background: theme.accentLight, color: theme.accentText }}>{tx.active}</span>
              : (
                <button type="button" className="st-session-end" onClick={() => showToast(tx.sessionEnded)}>
                  {tx.end}
                </button>
              )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="st-section">
      {notifRows.map((group) => (
        <div key={group.group} className="st-card">
          <div className="st-card-head">
            <span className="st-card-icon" style={{ background: theme.iconBg }}>
              <Icon name="bell" size={14} color="#fff" />
            </span>
            {group.group}
          </div>
          <div className="st-toggles">
            {group.items.map((item) => (
              <ToggleRow
                key={item.key}
                label={item.label}
                checked={notifs[item.key]}
                onChange={(v) => setNotifs((p) => ({ ...p, [item.key]: v }))}
                accent={theme.accent}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="st-form-actions">
        <button type="button" className="st-btn st-btn--primary" style={{ background: theme.accent }} onClick={saveNotifs}>
          <Icon name="check" size={14} color="#fff" /> {tx.saveNotifPrefs}
        </button>
      </div>
    </div>
  );

  const renderDisplay = () => (
    <div className="st-section">
      <div className="st-card">
        <div className="st-card-head">
          <span className="st-card-icon" style={{ background: theme.iconBg }}>
            <Icon name="settings" size={14} color="#fff" />
          </span>
          {tx.languageRegion}
        </div>
        <div className="st-form-grid">
          <div className="st-field">
            <label className="st-label">{tx.interfaceLang}</label>
            <select className="st-select" value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="ar">{tx.langArabic}</option>
              <option value="en">{tx.langEnglish}</option>
            </select>
          </div>
          <div className="st-field">
            <label className="st-label">{tx.dateFormat}</label>
            <select className="st-select">
              <option value="dmy">{tx.dateFmtDMY}</option>
              <option value="mdy">{tx.dateFmtMDY}</option>
              <option value="ymd">{tx.dateFmtYMD}</option>
            </select>
          </div>
          <div className="st-field">
            <label className="st-label">{tx.timezone}</label>
            <select className="st-select">
              <option value="riyadh">{tx.tzRiyadh}</option>
              <option value="dubai">{tx.tzDubai}</option>
              <option value="cairo">{tx.tzCairo}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="st-card">
        <div className="st-card-head">
          <span className="st-card-icon" style={{ background: theme.iconBg }}>
            <Icon name="eye" size={14} color="#fff" />
          </span>
          {tx.systemAppearance}
        </div>

        <div className="st-theme-picker">
          {[
            { id: 'light', label: tx.light, icon: '☀️' },
            { id: 'auto', label: tx.auto, icon: '🌓' },
            { id: 'dark', label: tx.dark, icon: '🌙' },
          ].map((opt) => (
            <button key={opt.id} type="button" className={`st-theme-opt${opt.id === 'light' ? ' st-theme-opt--active' : ''}`}
              style={opt.id === 'light' ? { borderColor: theme.accent, background: theme.accentLight } : {}}>
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="st-toggles" style={{ marginTop: 20 }}>
          <ToggleRow
            label={tx.animations}
            sub={tx.animationsSub}
            checked={animations}
            onChange={setAnimations}
            accent={theme.accent}
          />
          <ToggleRow
            label={tx.compact}
            sub={tx.compactSub}
            checked={compact}
            onChange={setCompact}
            accent={theme.accent}
          />
        </div>

        <div className="st-form-actions" style={{ marginTop: 20 }}>
          <button type="button" className="st-btn st-btn--primary" style={{ background: theme.accent }}
            onClick={() => savePreferences(tx.displaySaved)}>
            <Icon name="check" size={14} color="#fff" /> {tx.save}
          </button>
        </div>
      </div>
    </div>
  );

  const renderAbout = () => (
    <div className="st-section">
      <div className="st-about-hero" style={{ background: theme.headerBg }}>
        <img src="/logo.png" alt={tx.logoAlt} className="st-about-logo" />
        <div className="st-about-brand">
          <div className="st-about-name">Corp Pass</div>
          <div className="st-about-tagline">{tx.aboutTagline}</div>
        </div>
      </div>

      <div className="st-info-rows">
        {[
          { label: tx.version, value: 'v2.4.1' },
          { label: tx.releaseDate, value: tx.releaseDateVal },
          { label: tx.license, value: 'Enterprise' },
          { label: tx.currentRole, value: theme.label },
          { label: tx.branch, value: profile.branch },
          { label: tx.developer, value: tx.developerTeam },
          { label: tx.support, value: 'support@corppass.sa' },
        ].map((r) => (
          <div key={r.label} className="st-info-row">
            <span className="st-info-label">{r.label}</span>
            <span className="st-info-value">{r.value}</span>
          </div>
        ))}
      </div>

      <div className="st-about-links">
        {[tx.privacy, tx.terms, tx.contactSupport].map((l) => (
          <button key={l} type="button" className="st-about-link" style={{ color: theme.accent, borderColor: `${theme.accent}33` }}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );

  const RENDER = {
    profile: renderProfile,
    security: renderSecurity,
    notifications: renderNotifications,
    display: renderDisplay,
    about: renderAbout,
  };

  const activeKey = SECTIONS.find((s) => s.key === activeSection)?.key;

  return (
    <div className="st-page">
      {loadError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10 }}>{loadError}</div>}
      {loading && <div style={{ marginBottom: 12, color: '#78909c' }}>{tx.loading}</div>}

      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="st-header" style={{ background: theme.headerBg }}>
        <div className="st-header-content">
          <div className="st-header-icon">
            <Icon name="settings" size={20} color="#fff" strokeWidth={1.8} />
          </div>
          <div>
            <div className="st-header-title">{tx.settings}</div>
            <div className="st-header-sub">{tx.settingsSub}</div>
          </div>
        </div>
      </div>

      <div className="st-body">

        <nav className="st-nav">
          {SECTIONS.map((sec) => (
            <button
              type="button"
              key={sec.key}
              className={`st-nav-item${activeSection === sec.key ? ' st-nav-item--active' : ''}`}
              style={activeSection === sec.key ? { '--st-nav-accent': theme.accent, '--st-nav-light': theme.accentLight } : {}}
              onClick={() => setActive(sec.key)}
            >
              <span className="st-nav-item-icon">
                <Icon name={sec.icon} size={16}
                  color={activeSection === sec.key ? theme.accent : '#78909c'}
                  strokeWidth={activeSection === sec.key ? 2.2 : 1.8}
                />
              </span>
              <span className="st-nav-item-label">{sectionLabel(sec.key)}</span>
              {activeSection === sec.key && (
                <span className="st-nav-item-dot" style={{ background: theme.accent }} />
              )}
            </button>
          ))}
        </nav>

        <div className="st-content">
          <div className="st-content-head">
            <div className="st-content-icon" style={{ background: theme.iconBg }}>
              <Icon name={SECTIONS.find((s) => s.key === activeSection)?.icon} size={16} color="#fff" />
            </div>
            <div>
              <div className="st-content-title">
                {sectionLabel(activeKey)}
              </div>
              <div className="st-content-sub">
                Corp Pass / {tx.breadcrumbJoin} / {sectionLabel(activeKey)}
              </div>
            </div>
          </div>

          {RENDER[activeSection]?.()}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange, accent }) {
  return (
    <div className="st-toggle-row">
      <div className="st-toggle-info">
        <div className="st-toggle-label">{label}</div>
        {sub && <div className="st-toggle-sub">{sub}</div>}
      </div>
      <button
        type="button"
        className={`st-toggle${checked ? ' st-toggle--on' : ''}`}
        style={checked ? { background: accent } : {}}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
      >
        <span className="st-toggle-thumb" />
      </button>
    </div>
  );
}
